import { Button, Stack, TableContainer, Typography } from '@mui/material'
import PageContainer from '@/components/ui/containers/PageContainer'
import SelectInput from '@/components/ui/inputs/SelectInput'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  getStaffs,
  selectAdminUpdateRequestTracker,
  selectStaffs,
  updateEstablishmentSubscription,
  updateStaffSubscription,
} from '@/redux/reducers/admin.reducer'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import StaffsTable, { StaffData } from './component/StaffsTable'
import AutoModeIcon from '@mui/icons-material/AutoMode'
import { useRequestHandler } from '@/hooks/useRequestHandler'
import { useSnackbar } from 'notistack'
import { EstablishmentSubscription, SubscriptionType } from '@/utils/subscription/types'

export default function SubscriptionsPage() {
  const { establishmentId } = useParams()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [subscription, setSubscription] = useState<EstablishmentSubscription>(EstablishmentSubscription.ALL_ACCESS)

  const staffs = useAppSelector((state) => selectStaffs(state))

  const staffData = useMemo((): StaffData[] => {
    console.log(staffs[0])
    return staffs
      .filter((staff) => staff.establishment?._id === establishmentId)
      .map((staff) => ({
        id: staff._id,
        name: staff.userInfo.name,
        surname: staff.userInfo.surname,
        email: staff.email,
        status: staff.status,
        subscription: staff.subscription ? staff.subscription : SubscriptionType.MED_GUEST,
      }))
  }, [staffs, establishmentId])

  const { dispatch: handleDispatch } = useRequestHandler({
    requestTrackerSelector: selectAdminUpdateRequestTracker,
    onSuccess: () => {
      enqueueSnackbar(t('page.superAdmin.subscriptions.setSubscriptionButton.success'), { variant: 'success' })
      dispatch(
        getStaffs({
          etablissement: establishmentId,
        })
      )
    },
    onError: () => {
      enqueueSnackbar(t('page.superAdmin.subscriptions.setSubscriptionButton.error'), { variant: 'error' })
    },
  })

  useEffect(() => {
    // fetch all staffs for this establishment
    dispatch(
      getStaffs({
        etablissement: establishmentId,
      })
    )
  }, [dispatch, establishmentId])

  const updateSubscription = () => {
    if (!establishmentId) return
    handleDispatch(
      updateEstablishmentSubscription({
        etablissementId: establishmentId,
        subscription,
      })
    )
  }

  const handleIndivualSubscriptionChange = useCallback(
    (staff: StaffData, subscription: SubscriptionType) => {
      handleDispatch(
        updateStaffSubscription({
          staffId: staff.id,
          subscription: subscription,
        })
      )
    },
    [handleDispatch]
  )

  if (!establishmentId) return null

  return (
    <PageContainer>
      <Stack spacing={2}>
        <Typography variant="h4">{t('page.superAdmin.subscriptions.title')}</Typography>
        <Stack direction="row" spacing={2}>
          <SelectInput
            name="subscription"
            options={Object.values(EstablishmentSubscription).map((value) => {
              return { value, label: value }
            })}
            value={subscription}
            onChange={(e) => {
              setSubscription(e.target.value as EstablishmentSubscription)
            }}
          />
          <Button variant="contained" color="primary" startIcon={<AutoModeIcon />} onClick={updateSubscription}>
            {t('page.superAdmin.subscriptions.setSubscriptionButton.label')}
          </Button>
        </Stack>
        <TableContainer>
          <StaffsTable data={staffData} onSubscriptionChange={handleIndivualSubscriptionChange} />
        </TableContainer>
      </Stack>
    </PageContainer>
  )
}
